import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header.jsx';
import {
  Box,
  Typography,
  Snackbar,
  CircularProgress,
  Alert,
  MenuItem
} from '@mui/material';
import { CustomTextFieldMultiLine, CustomTextField } from '../components/Inputs.jsx';
import { STPButton } from '../components/Botones.jsx';
import { STPSelect, STPInputLabel } from '../components/Select.jsx';
import { sendMail } from '../services/sendMail.js';
import { getWelcomeCourses } from '../services/getWelcomeCourses.js';
import { STPDescription } from '../components/STPDescription.jsx';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

export function Contacta({ setIsAuthenticated }) {
  /* ────────────────────────────────────────
   * STATE
   * ──────────────────────────────────────── */
  const [comments, setComments] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState('success');
  const [loading, setLoading] = useState(false);

  const [isAuthenticated, setIsAuth] = useState(null);
  const [toMatricular, setToMatricular] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    course: '',       // id del curso seleccionado
    courseGroup: '',  // id del grupo seleccionado
    message: ''
  });

  const [showErrors, setShowErrors] = useState(false);
  const [userChangedMessage, setUserChangedMessage] = useState(false);

  const [courses, setCourses] = useState([]);        // lista de cursos sueltos
  const [coursesGroup, setCoursesGroup] = useState(null); // objeto grupo o null

  const { t, i18n } = useTranslation();

  /* ────────────────────────────────────────
   * EFFECT: leer parámetros de la URL y precargar datos
   * ──────────────────────────────────────── */
  useEffect(() => {
    const urlParams        = new URLSearchParams(window.location.search);
    const toMatricularParam = urlParams.get('toMatricular') === 'true';
    const courseIdParam    = urlParams.get('courseId');
    const groupIdParam     = urlParams.get('groupId');

    setToMatricular(toMatricularParam);

    const fetchCourses = async () => {
      try {
        /* ───── URL con groupId → un único grupo de cursos ───── */
        if (groupIdParam) {
          const { data: group } = await axios.get(
            `${import.meta.env.VITE_APP_API_URL}welcome-courses?groupId=${groupIdParam}`
          );

          setCoursesGroup(group);

          if (group?.id) {
            handleFormChange('courseGroup', group.id);
          }

        /* ───── URL con courseId → un solo curso ───── */
        } else if (courseIdParam) {
          const { data: c } = await axios.get(
            `${import.meta.env.VITE_APP_API_URL}welcome-courses?courseId=${courseIdParam}`
          );

          const formatted = [{ value: c.idcurso, label: c.descripcion }];
          setCourses(formatted);
          handleFormChange('course', formatted[0].value);

        /* ───── Sin parámetros → lista completa de cursos ───── */
        } else {
          const list = await getWelcomeCourses();
          const formatted = list.map(c => ({
            value: c.idcurso,
            label: c.descripcion
          }));
          setCourses(formatted);
        }
      } catch (err) {
        console.error('Error fetching courses:', err);
      }
    };

    fetchCourses();
  }, []); // [] => sólo al montar el componente

  // Cambia el idioma si viene en la URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const lngParam = urlParams.get('lng');
    if (lngParam && i18n.language !== lngParam) {
      i18n.changeLanguage(lngParam);
    }
  }, [i18n]);

  // Genera el mensaje automático cuando courses o coursesGroup y formData.course/courseGroup están disponibles
  useEffect(() => {
    // Para grupo de cursos
    if (
      formData.courseGroup &&
      coursesGroup &&
      !userChangedMessage
    ) {
      let groupName = formData.courseGroup;
      if (Array.isArray(coursesGroup)) {
        const found = coursesGroup.find(g => String(g.id) === String(formData.courseGroup));
        if (found) groupName = found.nombre;
      } else if (coursesGroup && String(coursesGroup.id) === String(formData.courseGroup)) {
        groupName = coursesGroup.nombre;
      }
      setFormData(prev => ({
        ...prev,
        message: t('contact.defaultGroupMessage', { groupName })
      }));
    }
    // Para curso individual
    else if (
      formData.course &&
      courses &&
      !userChangedMessage
    ) {
      let courseName = formData.course;
      let horario = '';
      let codeje = '';
      const found = courses.find(c => String(c.value) === String(formData.course));
      if (found) {
        courseName = found.label;
        if (found.horario) {
          horario = found.horario;
        } else if (window.location.search.includes('horario=')) {
          const params = new URLSearchParams(window.location.search);
          horario = decodeURIComponent(params.get('horario') || '');
          codeje = decodeURIComponent(params.get('codeje') || '');
        }
      }
      setFormData(prev => ({
        ...prev,
        message: t('contact.defaultCourseMessage', { courseName, horario: horario, codeje: codeje })
      }));
    }
  }, [formData.courseGroup, coursesGroup, formData.course, courses, userChangedMessage, t, i18n.language]);

  /* ────────────────────────────────────────
   * HANDLERS
   * ──────────────────────────────────────── */
  const handleChange = (value) => setComments(value);

  /**
   *  Actualiza formData garantizando que el valor de course/courseGroup
   *  sea SIEMPRE el id, no la descripción.
   */
  const handleFormChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };

      /* ── Mensaje automático solo si el usuario aún no lo ha tocado ── */
      if (!userChangedMessage) {
        if (field === 'course' && value) {
          // Ya no generamos el mensaje aquí, lo hace el useEffect
        }

      }

      return newData;
    });
  };

  /* ────────────────────────────────────────
   * ENVÍO DEL FORMULARIO
   * ──────────────────────────────────────── */
  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setShowErrors(true);

    /* ── USUARIO LOGEADO ── */
    if (isAuthenticated) {
      if (!comments.trim()) {
        setSnackbarType('warning');
        setSnackbarOpen(true);
        setSnackbarMessage(t('contact.emptyComments'));
        setLoading(false);
        return;
      }

      sendMail({ comentarios: comments })
        .then(response => {
          if (response) {
            setSnackbarType('success');
            setSnackbarOpen(true);
            setSnackbarMessage(t('contact.success'));
            setComments(t('contact.infoMessage'));
            setLoading(false);
            setFormData({ name: '', email: '', course: '', courseGroup: '', message: '' });
            setShowErrors(false);
          }
        })
        .catch(error => {
          console.error('Error sending email:', error);
          setSnackbarOpen(true);
          setSnackbarMessage(t('contact.error'));
          setLoading(false);
        });

    /* ── USUARIO NO LOGEADO ── */
    } else {
      if (!formData.name || !formData.email || !formData.message.trim()) {
        setSnackbarType('warning');
        setSnackbarOpen(true);
        setSnackbarMessage(t('contact.fillRequiredFields'));
        setLoading(false);
        return;
      }

      /* Formato del email */
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setSnackbarType('warning');
        setSnackbarOpen(true);
        setSnackbarMessage('Por favor, introduce un email válido');
        setLoading(false);
        return;
      }

      /* Prefijo dinámico para el mensaje */
      let messagePrefix = '';
      if (formData.course) {
        const sel = courses.find(c => c.value === formData.course);
        messagePrefix = `Estoy interesado en el curso ${sel?.label ?? formData.course}`;
      } else if (formData.courseGroup && coursesGroup) {
        messagePrefix = `Estoy interesado en el grupo de cursos ${coursesGroup.nombre}`;
      } else {
        messagePrefix = 'Estoy interesado en el curso/grupo relacionado';
      }

      sendMail({
        comentarios: `${messagePrefix}. ${formData.message}`,
        name: formData.name,
        email: formData.email,
        course: formData.course || formData.courseGroup
      })
        .then(response => {
          if (response) {
            setSnackbarType('success');
            setSnackbarOpen(true);
            setSnackbarMessage(t('contact.success'));
            setFormData({ name: '', email: '', course: '', courseGroup: '', message: '' });
            setLoading(false);
            setShowErrors(false);
          }
        })
        .catch(error => {
          console.error('Error sending email:', error);
          setSnackbarOpen(true);
          setSnackbarMessage(t('contact.error'));
          setLoading(false);
        });
    }
  };

  /* ────────────────────────────────────────
   * RENDER
   * ──────────────────────────────────────── */
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress sx={{ color: 'rgba(80, 165, 121, 0.7)' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', width: '100%' }}>
      <Header setIsAuthenticated={setIsAuthenticated} />

      <Box sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="h4" sx={{ mb: 3, color: 'black', textAlign: 'center', fontWeight: 'bold' }}>
          {toMatricular ? t('contact.enrollTitle') : t('menuItems.contact')}
        </Typography>

        {/* ────────────────────────────
         * FORMULARIO USUARIO AUTENTICADO
         * ──────────────────────────── */}
        {isAuthenticated ? (
          <>
            <CustomTextFieldMultiLine
              fullWidth
              label={t('contact.sendComments')}
              variant="outlined"
              margin="normal"
              size="md"
              value={comments}
              onChange={(e) => handleChange(e.target.value)}
            />
            <STPButton sx={{ mt: 2 }} onClick={handleSubmit}>
              {t('contact.send')}
            </STPButton>
          </>

        /* ────────────────────────────
         * FORMULARIO USUARIO NO AUTENTICADO
         * ──────────────────────────── */
        ) : (
          <Box sx={{ width: '100%', maxWidth: 600 }}>
            <CustomTextField
              fullWidth
              label={t('contact.name') + ' *'}
              variant="outlined"
              margin="normal"
              value={formData.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              required
              error={showErrors && !formData.name}
              helperText={showErrors && !formData.name ? t('contact.nameRequired') : ''}
            />

            <CustomTextField
              fullWidth
              label={t('contact.email') + ' *'}
              variant="outlined"
              margin="normal"
              type="email"
              value={formData.email}
              onChange={(e) => handleFormChange('email', e.target.value)}
              required
              error={showErrors && !formData.email}
              helperText={showErrors && !formData.email ? t('contact.emailRequired') : ''}
            />

            {/* Select visible sólo si viene de Welcome */}
            {toMatricular && (
              <Box sx={{ width: '100%', mb: 2 }}>
                <STPInputLabel sx={{ ml: 1.5, color: 'black', fontWeight: 'bold' }}>
                  {coursesGroup ? t('contact.groupLabel', 'Grupo de cursos') : t('contact.courseLabel', 'Curso')}
                </STPInputLabel>
                <STPSelect
                  fullWidth
                  value={coursesGroup ? formData.courseGroup : formData.course}
                  onChange={(e) => {
                    if (coursesGroup) {
                      handleFormChange('courseGroup', e.target.value);
                    } else {
                      handleFormChange('course', e.target.value);
                    }
                  }}
                  label={coursesGroup ? t('contact.groupLabel', 'Grupo de cursos') : t('contact.courseLabel', 'Curso')}
                >
                  {coursesGroup ? (
                    <MenuItem key={coursesGroup.id} value={coursesGroup.id}>
                      {coursesGroup.nombre}
                    </MenuItem>
                  ) : (
                    courses.map(course => (
                      <MenuItem key={course.value} value={course.value}>
                        {course.label}
                      </MenuItem>
                    ))
                  )}
                </STPSelect>
              </Box>
            )}

            <CustomTextFieldMultiLine
              fullWidth
              label={t('contact.message') + ' *'}
              variant="outlined"
              margin="normal"
              size="md"
              value={formData.message}
              onChange={(e) => {
                handleFormChange('message', e.target.value.trim());
                setUserChangedMessage(true);
              }}
              required
              error={showErrors && !formData.message.trim()}
              helperText={showErrors && !formData.message.trim() ? t('contact.messageRequired') : ''}
            />

            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 2 }}>
              <STPButton onClick={handleSubmit}>{t('contact.send')}</STPButton>
            </Box>
          </Box>
        )}

        {/* ────────────────────────────
         * SNACKBAR
         * ──────────────────────────── */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
        >
          <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarType} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>

        <STPDescription mt={4} />
      </Box>
    </Box>
  );
}
